import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { SignIn } from '@clerk/clerk-react'
import {
  apiPost,
  isApiError,
  setStoredStudentToken,
} from '../api/client'
import { useLocalizedPath, useT } from '../i18n'
import SchoolSearchPicker from './SchoolSearchPicker'
import AnimatedEehaeWordmark from './AnimatedEehaeWordmark'
import QuestionBubbles from './QuestionBubbles'
import TestingPasswordPanel from './TestingPasswordPanel'
// CSS Module — component-internal styles. The shared ``.splash``,
// ``.splash-center``, ``.splash-logo*`` chrome stays global (in
// ``styles/splash-chrome.css``); see #264 PR A.
import s from './SplashScreen.module.css'

// How long the "signed out" toast stays on screen after a sign-out redirect.
const TOAST_MS = 4000

const STUDENT_DATA_KEY = 'studentLoginData'

// Testing-mode awareness (mirror of the gate logic in TestingModeGate
// + SiteGate). When VITE_TESTING_MODE=1 AND the user hasn't entered
// the site password, the splash hides its CTAs + admin pill and
// shows the testing notice in their place — the page is still
// publicly browseable (corporate-website requirement) but offers no
// path into the gated app surface. Removed alongside the rest of
// the testing-mode machinery at public launch.
//
// Read at call time (not module load) so tests can flip the value
// per-case via vi.stubEnv. Vite still inlines the value at build
// time for production, so there's no runtime cost in the deployed
// bundle — the env-read becomes a constant.
const isTestingModeOn = () => import.meta.env.VITE_TESTING_MODE === '1'
const SITE_PASSWORD_KEY = 'siteAccess'

interface LoginResponse {
  student_pk: number
  student_id: string
  // Issue #1096 / ADR 0051 Phase 1 — nullable for a class-less student (zero
  // enrollments). Only used to persist the login snapshot; the picker reads
  // it back tolerant of null.
  class_code: string | null
  token: string
  token_expires_at: string
  modules: unknown[]
  // PIPA P2 (#128) chunk 2 backend → chunk 3 frontend wiring.
  // True when no EulaAcceptance exists for the student at the
  // current POLICY_VERSION — the Art. 22-2 §3 notice screen must
  // fire before the picker. Default to false on missing (older
  // backend builds OR a hot-path through dev-quickstart that
  // bypasses the notice).
  notice_required?: boolean
  // Issue #967 — True when the student authenticated via the legacy
  // ``class_code`` bootstrap path (no password yet). The login handler
  // routes a True straight to the set-password screen instead of the
  // picker. Default false on missing so the password path + pre-#967
  // backends treat it as a no-op.
  password_setup_required?: boolean
}

/**
 * Splash screen + integrated student login.
 *
 * The splash has three modes:
 *
 * - ``cta``  — shows the two primary CTAs (teacher dashboard, student
 *              entry). The default state.
 * - ``login`` — student CTA flips the same surface into a login form.
 *               The gradient + QuestionBubbles never re-mount; the logo
 *               just downscales via CSS, the CTAs fade out, the form
 *               fades in. URL stays at ``/`` throughout — login is a
 *               transient state of the splash, not a separate route.
 * - ``admin-signin`` — admin pill triggers an in-place transition: the
 *               splash content (logo + buttons) fades out, the admin
 *               marketing copy fades in on the left, and a paper-cream
 *               panel slides in from the right hosting Clerk's
 *               ``<SignIn routing="virtual" />``. The URL stays at
 *               ``/`` throughout; deep-linking to ``/admin/sign-in``
 *               still works and renders the AuthLayout split-screen.
 *
 * On successful login we navigate to ``/student`` (or ``/en/student``)
 * which is now picker-only. After admin sign-in, Clerk redirects to
 * ``/admin``.
 */
export default function SplashScreen() {
  const t = useT()
  const lp = useLocalizedPath()
  const navigate = useNavigate()
  // Initial value lazy-resolved from the URL: if we landed here with
  // ?signed-out=1, the toast is visible from the first render rather
  // than flickering in via a mount effect. The mount effect below
  // still scrubs the URL param + schedules the auto-hide timer.
  const [signedOutToast, setSignedOutToast] = useState(
    () => new URLSearchParams(window.location.search).get('signed-out') === '1',
  )

  // #832 — not-authorized toast. Fired when a signed-in non-admin user
  // is redirected here from an admin route. Same lazy-init + effect
  // pattern as signedOutToast above.
  const [notAuthorizedToast, setNotAuthorizedToast] = useState(
    () =>
      new URLSearchParams(window.location.search).get('not-authorized') === '1',
  )

  // Mode flips the splash between CTA, student-login, and admin-signin
  // states. URL doesn't capture this — refresh always resets to CTA,
  // which is fine: the canonical splash is the CTA state, login and
  // admin-signin are transient affordances.
  const [mode, setMode] = useState<'cta' | 'login' | 'admin-signin'>('cta')
  // Issue #967 — the splash login form has two sub-forms:
  //   - ``password`` (default): the new per-student-password path
  //     (school_code + student_id + password).
  //   - ``bootstrap``: the legacy first-login path (class_code +
  //     student_id, no password) for a student who hasn't set one yet.
  // The "first time? set up your password" affordance toggles between
  // them WITHOUT leaving login mode — same surface, same fade chrome.
  // ADR 0051 Phase 4 added the ``forgot`` sub-form (in-app reset-request
  // queue). All three share the same fade chrome / surface.
  const [loginMode, setLoginMode] = useState<
    'password' | 'bootstrap' | 'forgot'
  >('password')
  const [schoolCode, setSchoolCode] = useState('')
  const [classCode, setClassCode] = useState('')
  const [studentId, setStudentId] = useState('')
  const [password, setPassword] = useState('')
  const [loginError, setLoginError] = useState('')
  const [loginSubmitting, setLoginSubmitting] = useState(false)
  // ADR 0051 Phase 4 — flips true once the forgot-password request is sent;
  // the form is replaced by a generic confirmation (no enumeration oracle —
  // same message whether or not the id resolved server-side).
  const [forgotSubmitted, setForgotSubmitted] = useState(false)

  // Testing-mode block: when active, the CTAs + admin pill are hidden
  // and replaced with the inline testing notice. Initialized from
  // localStorage so a returning visitor with a valid bypass goes
  // straight to the full splash without flash. Cleared by the
  // TestingPasswordPanel's onUnlocked callback when a password is
  // accepted.
  const [testingModeBlocked, setTestingModeBlocked] = useState<boolean>(
    () => isTestingModeOn() && !localStorage.getItem(SITE_PASSWORD_KEY),
  )

  // Auto-focus target: the first input of whichever sub-form is active.
  // Re-pointed when the login sub-form toggles so focus follows the
  // student into the form they switched to.
  const firstFieldRef = useRef<HTMLInputElement>(null)

  // Sign-out redirects from teacher/admin pages land here with
  // `?signed-out=1`. The toast's initial visibility is read from the
  // URL via lazy-init useState above; this effect handles cleanup:
  // scrub the URL param so a refresh doesn't re-show, and schedule
  // the auto-hide timer.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('signed-out') !== '1') return
    params.delete('signed-out')
    const qs = params.toString()
    const cleanedUrl =
      window.location.pathname +
      (qs ? `?${qs}` : '') +
      window.location.hash
    window.history.replaceState({}, '', cleanedUrl)
    const id = setTimeout(() => setSignedOutToast(false), TOAST_MS)
    return () => clearTimeout(id)
  }, [])

  // #832 — not-authorized toast cleanup. Scrubs ?not-authorized=1 from
  // the URL so a refresh doesn't re-show the toast, and schedules the
  // auto-hide timer. Mirrors the signedOutToast effect above.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('not-authorized') !== '1') return
    params.delete('not-authorized')
    const qs = params.toString()
    const cleanedUrl =
      window.location.pathname +
      (qs ? `?${qs}` : '') +
      window.location.hash
    window.history.replaceState({}, '', cleanedUrl)
    const id = setTimeout(() => setNotAuthorizedToast(false), TOAST_MS)
    return () => clearTimeout(id)
  }, [])

  // Cross-tab + in-tab unlock sync for testingModeBlocked (#706).
  //
  // The in-tab same-component path is already covered: the inline
  // <TestingPasswordPanel onUnlocked> callback flips state directly
  // (line ~366). But two unlock paths bypass that callback:
  //
  //   1. Cross-tab: another tab writes localStorage[SITE_PASSWORD_KEY]
  //      (after a successful /api/probe). The native ``storage`` event
  //      fires in every OTHER tab — mirrors the shape in SiteGate.tsx:118-125.
  //
  //   2. In-tab sibling: a future SiteGate prompt path (or any other
  //      dispatcher) fires ``sitegate:unlocked`` without unmounting
  //      SplashScreen — mirrors SiteGate.tsx:91-95.
  //
  // Both effects read from the event itself rather than from the
  // React closure to avoid stale-capture bugs (same rationale as the
  // CodeRabbit note on PR #271 / SiteGate.tsx:104-110).
  useEffect(() => {
    const onUnlock = () => setTestingModeBlocked(false)
    window.addEventListener('sitegate:unlocked', onUnlock)
    return () => window.removeEventListener('sitegate:unlocked', onUnlock)
  }, [])

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== SITE_PASSWORD_KEY) return
      setTestingModeBlocked(isTestingModeOn() && !e.newValue)
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  // Auto-focus the first field once the login form transitions in.
  // Small delay so focus lands AFTER the fade-in completes; otherwise
  // the input flashes into view already focused, which feels jumpy.
  // Re-runs on ``loginMode`` so toggling to the bootstrap sub-form moves
  // focus to its first field (class code) too.
  useEffect(() => {
    if (mode !== 'login') return
    const id = window.setTimeout(() => {
      firstFieldRef.current?.focus()
    }, 380)
    return () => window.clearTimeout(id)
  }, [mode, loginMode])

  // Track whether the user has scrolled into the marketing extension
  // below the splash. When they have, fade out the floating admin pill
  // so it doesn't overlap the marketing sticky-nav that fades IN at
  // the same threshold.
  //
  // We observe the same ``#mkt-splash-sentinel`` element MarketingExtension
  // uses for its sticky-nav reveal — keeping both reveals tied to one
  // sentinel guarantees they stay perfectly synchronized regardless of
  // viewport height. If the sentinel doesn't exist (e.g. SplashScreen
  // is rendered standalone without the marketing extension below it),
  // the effect no-ops and the admin pill stays visible.
  const [pastSplash, setPastSplash] = useState(false)
  useEffect(() => {
    const sentinel = document.getElementById('mkt-splash-sentinel')
    if (!sentinel) return
    const obs = new IntersectionObserver(
      ([entry]) => setPastSplash(!entry.isIntersecting),
      { threshold: 0 },
    )
    obs.observe(sentinel)
    return () => obs.disconnect()
  }, [])

  const handleEnterLogin = (e: React.MouseEvent) => {
    e.preventDefault()
    setMode('login')
  }

  // Admin pill click handler — intercepts the navigation and flips the
  // splash into admin-signin mode. preventDefault keeps the URL at /;
  // right-click "open in new tab" still goes to /admin (because the
  // Link's href is preserved), so deep-linking and accessibility stay
  // intact.
  const handleEnterAdminSignin = (e: React.MouseEvent) => {
    e.preventDefault()
    setMode('admin-signin')
  }

  const handleBackToCta = () => {
    setMode('cta')
    setLoginError('')
    // Reset the sub-form to the default (password) path so re-entering
    // login mode always starts on the primary form.
    setLoginMode('password')
    setForgotSubmitted(false)
  }

  // Toggle between the password sub-form and the legacy bootstrap
  // sub-form. Clear any standing error so a failure on one form doesn't
  // linger on the other. (From the ``forgot`` sub-form this returns to
  // password — the toggle label reflects that.)
  //
  // All three sub-form switch handlers below bail while a submit is in
  // flight: the toggle links stay clickable during a submit (only the
  // submit button disables), so without the guard a stale completion from
  // the PREVIOUS sub-form would paint its error (e.g. "invalid
  // credentials") onto the newly-visible form.
  const handleToggleLoginMode = () => {
    if (loginSubmitting) return
    setLoginMode((m) => (m === 'password' ? 'bootstrap' : 'password'))
    setLoginError('')
    // The school picker remounts across this transition with a fresh (empty)
    // visible text; clear the resolved code too so the blank field can't
    // silently carry a stale login_code into the next submit.
    setSchoolCode('')
  }

  // ADR 0051 Phase 4 — enter / leave the forgot-password sub-form. Resets the
  // submitted-confirmation flag so a second visit starts on the form again, and
  // clears schoolCode so the (remounted, blank) picker doesn't retain a
  // carried-over code behind an empty field.
  const handleEnterForgot = () => {
    if (loginSubmitting) return
    setLoginMode('forgot')
    setLoginError('')
    setForgotSubmitted(false)
    setSchoolCode('')
  }
  const handleExitForgot = () => {
    if (loginSubmitting) return
    setLoginMode('password')
    setLoginError('')
    setForgotSubmitted(false)
    setSchoolCode('')
  }

  // Per-sub-form completeness gate for the submit button + early return.
  const isLoginComplete =
    loginMode === 'password'
      ? !!schoolCode.trim() && !!studentId.trim() && !!password
      : loginMode === 'bootstrap'
        ? !!classCode.trim() && !!studentId.trim()
        : // forgot — school + id, no password
          !!schoolCode.trim() && !!studentId.trim()

  // ADR 0051 Phase 4 — fire the in-app reset request. Enumeration-safe: the
  // backend always 200s and only writes a row on a real match, so the UI shows
  // the SAME confirmation regardless. A thrown error here is a transport
  // failure (not "id not found"), so a generic retry hint leaks no oracle.
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isLoginComplete) return
    setLoginError('')
    setLoginSubmitting(true)
    try {
      await apiPost('/auth/student-forgot-password', {
        school_code: schoolCode.trim(),
        student_id: studentId.trim(),
      })
      setForgotSubmitted(true)
    } catch {
      setLoginError(t('forgot.error'))
    } finally {
      setLoginSubmitting(false)
    }
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isLoginComplete) return
    setLoginError('')
    setLoginSubmitting(true)
    try {
      // Issue #967 — dual-path login. The password sub-form posts
      // ``{school_code, student_id, password}`` (the new per-student
      // password path); the bootstrap sub-form posts
      // ``{class_code, student_id}`` (legacy first-login, only valid
      // while the student has no password yet). The backend selects the
      // path on the presence of ``password``.
      const body =
        loginMode === 'password'
          ? {
              school_code: schoolCode.trim(),
              student_id: studentId.trim(),
              password,
            }
          : {
              class_code: classCode.trim(),
              student_id: studentId.trim(),
            }
      const data = await apiPost<LoginResponse>('/auth/student-login', body)
      setStoredStudentToken(data.token)
      localStorage.setItem(STUDENT_DATA_KEY, JSON.stringify(data))
      // Issue #967 — a legacy-bootstrap login lands a passwordless
      // student on the set-password screen FIRST (the backend sets
      // ``password_setup_required: true`` only on that path). This
      // takes precedence over the notice gate below: the student sets a
      // password, then the picker's mount-time /student-me refresh
      // surfaces ``notice_required`` and routes to the notice if still
      // pending. Default false so the password path + pre-#967 backends
      // skip straight to the existing notice/picker flow.
      if (data.password_setup_required ?? false) {
        navigate(lp('/student/set-password'))
        return
      }
      // PIPA P2 (#128) chunk 3 — Art. 22-2 §3 notice gate. Soft
      // gate: the token is already valid (issued by chunk-2's
      // student_login regardless of acceptance state); we route
      // to the notice screen so the student sees the legally-
      // required plain-language summary before landing on the
      // picker. The notice page POSTs to
      // /api/auth/student-acknowledge-notice and then navigates to
      // /student itself. Pre-chunk-3 frontends (and tests that
      // don't care about the flag) treat ``notice_required`` as
      // false (default ``?? false`` below) and route straight to
      // the picker — the chunk-2 backend default is also false on
      // an already-accepted row.
      if (data.notice_required ?? false) {
        navigate(lp('/student/notice'))
      } else {
        navigate(lp('/student'))
      }
    } catch (err: unknown) {
      // Issue #1104 — a 401 is the credential-failure case: the backend
      // detail is the intentionally opaque English "Invalid login
      // credentials" (routers/auth.py::_INVALID_LOGIN), so render the
      // localized generic message instead of the raw detail. Other
      // failures (504 timeout, network TypeError) keep their own message
      // — they're transport problems, not bad credentials. The bootstrap
      // sub-form shares this catch, so its 401 shows the same generic
      // copy (naming school code/password it doesn't render) — accepted
      // as still oracle-free pending the Part B resolution of that form.
      const message =
        isApiError(err) && err.status === 401
          ? t('login.error.invalidCredentials')
          : err instanceof Error
            ? err.message
            : t('login.error.invalidCredentials')
      setLoginError(message)
    } finally {
      setLoginSubmitting(false)
    }
  }

  return (
    <div
      // ``splash`` (global chrome) + per-mode modifier classes (hashed
      // via the module). The mode classes drive the in-place
      // login / admin-signin transitions; selectors that reach into
      // the global chrome use ``:global()`` inside the module.
      className={[
        'splash',
        mode === 'login' && s.splashLogin,
        mode === 'admin-signin' && s.splashAdminSignin,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <QuestionBubbles />

      {/* Admin entry point — tucked in the upper-right corner so it
          doesn't crowd the two main CTAs. IT staff go here to provision
          a school; teachers and students never need this link.

          Click flips the splash into admin-signin mode (in-place
          transition); the Link's href is preserved so right-click
          "open in new tab" still routes to /admin/sign-in normally.

          Fades out (with pointer-events disabled) once the user scrolls
          past the splash and into the marketing extension below — the
          marketing sticky-nav that reveals at the same threshold would
          otherwise be obscured by this pill on small viewports. Also
          hidden when admin-signin mode is active (the back button
          replaces it). */}
      {/* Admin pill is hidden entirely when testingModeBlocked — public
          visitors see no path into the app surface. Hidden via the
          ``{!testingModeBlocked && ...}`` guard rather than the existing
          opacity transition because the goal here is structural (no
          such control exists for public visitors), not "scrolled-past"
          fade-out. */}
      {!testingModeBlocked && (
        <Link
          to="/admin"
          onClick={handleEnterAdminSignin}
          aria-label={t('splash.admin')}
          style={{
            position: 'fixed',
            top: 12,
            right: 12,
            zIndex: 9999,
            background: '#fff',
            color: '#444',
            border: '1px solid #bbb',
            borderRadius: 999,
            padding: '4px 12px',
            font: '12px/1.3 system-ui, sans-serif',
            textDecoration: 'none',
            boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
            opacity: pastSplash || mode === 'admin-signin' ? 0 : 1,
            pointerEvents:
              pastSplash || mode === 'admin-signin' ? 'none' : 'auto',
            transition: 'opacity 0.3s ease',
          }}
        >
          {t('splash.admin')}
        </Link>
      )}

      {/* Back button shown in login OR admin-signin mode — returns to
          CTA without leaving the page. Hidden in CTA mode. */}
      {(mode === 'login' || mode === 'admin-signin') && (
        <button
          type="button"
          onClick={handleBackToCta}
          className={s.back}
          aria-label={t('splash.back')}
        >
          ← {t('splash.back')}
        </button>
      )}

      {signedOutToast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            top: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#d1fae5',
            color: '#065f46',
            border: '1px solid #6ee7b7',
            padding: '8px 16px',
            borderRadius: 8,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            font: '14px/1.4 system-ui, sans-serif',
            zIndex: 10000,
          }}
        >
          {t('splash.signedOut')}
        </div>
      )}

      {/* #832 — not-authorized toast. Shown when a non-admin signed-in
          user is redirected here from an admin route. Amber/warning
          palette (vs. green for sign-out) to distinguish the reason. */}
      {notAuthorizedToast && (
        <div
          role="alert"
          aria-live="assertive"
          style={{
            position: 'fixed',
            top: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#fef3c7',
            color: '#92400e',
            border: '1px solid #fcd34d',
            padding: '8px 16px',
            borderRadius: 8,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            font: '14px/1.4 system-ui, sans-serif',
            zIndex: 10000,
          }}
        >
          {t('splash.notAuthorized')}
        </div>
      )}

      {/* ``splash-center`` + ``splash-logo-wrapper`` are global chrome
          (shared with TestingModeGate via ``site-gates.css``). They
          live in ``styles/splash-chrome.css``. The inner mark is the
          full ``eehae`` wordmark composed of animated SVG letterforms
          (e+e+h+a+e); the wrapper's clip-path-driven write-in (still
          applied to ``.splash-logo`` for TestingModeGate's text usage)
          is replaced here by the wordmark's own per-letter
          stroke-dashoffset reveal. */}
      <div className="splash-center">
        <div className="splash-logo-wrapper">
          <AnimatedEehaeWordmark className={s.splashMark} />
        </div>

        {/* Single subtitle that swaps text content based on mode. Only
            one element in flow so the layout stays clean — no double-
            margins, no negative-margin overlap tricks. The CTA voice
            line shows by default; in login mode the brand line fades
            out and the login subtitle (rendered inside the form
            below) takes its place visually. */}
        <p
          className={[s.subtitle, mode === 'login' && s.subtitleFaded]
            .filter(Boolean)
            .join(' ')}
        >
          {t('splash.subtitle')}
        </p>

        {/* Mode panels — both absolutely positioned within
            .splash-modes; .splash-modes has an animated min-height
            that swaps between buttons-height (CTA) and form-height
            (login), driving smooth flex-recentering as the cluster
            grows / shrinks.

            When testingModeBlocked: the entire CTA + login cluster is
            replaced with the inline TestingPasswordPanel. Public
            visitors see the testing notice in the buttons area —
            the badge, the bilingual message, and the discreet
            "I have a password" reveal that lets a tester self-bypass
            without leaving the splash. ``mode`` stays at ``'cta'``
            in this branch (no path exists to flip it without the
            admin pill / student CTA), so admin-overlay rendering
            below is also implicitly suppressed by aria-hidden. */}
        {testingModeBlocked ? (
          <div className={`${s.modes} ${s.modesTesting}`}>
            <TestingPasswordPanel
              onUnlocked={() => setTestingModeBlocked(false)}
            />
          </div>
        ) : (
          <div className={s.modes}>
            <div
              className={[
                s.mode,
                s.modeCta,
                mode === 'login' && s.modeHidden,
              ]
                .filter(Boolean)
                .join(' ')}
              aria-hidden={mode === 'login'}
            >
              <div className={s.buttons}>
                {/* ``data-testid`` exposed for student_login_workflow
                    spec — the class names are now hashed so the spec
                    can't pin to ``.splash-btn-secondary`` anymore. */}
                <Link
                  to={lp('/teacher')}
                  className={`${s.btn} ${s.btnPrimary}`}
                  data-testid="splash-teacher-cta"
                  tabIndex={mode === 'login' ? -1 : 0}
                >
                  {t('splash.teacher')}
                </Link>
                <a
                  href={lp('/student')}
                  onClick={handleEnterLogin}
                  className={`${s.btn} ${s.btnSecondary}`}
                  data-testid="splash-student-cta"
                  tabIndex={mode === 'login' ? -1 : 0}
                >
                  {t('splash.student')}
                </a>
              </div>
            </div>

            <form
              className={[
                s.mode,
                s.modeLogin,
                mode === 'login' && s.modeActive,
              ]
                .filter(Boolean)
                .join(' ')}
              data-testid="splash-login-form"
              onSubmit={
                loginMode === 'forgot' ? handleForgotPassword : handleLogin
              }
              aria-hidden={mode !== 'login'}
            >
              <p className={s.loginSubtitle}>
                {loginMode === 'password'
                  ? t('splash.loginSubtitle')
                  : loginMode === 'bootstrap'
                    ? t('splash.loginSubtitleBootstrap')
                    : t('splash.loginSubtitleForgot')}
              </p>
              {loginMode === 'password' ? (
                <>
                  {/* ADR 0051 Phase 4 — school-name search picker replaces the
                      raw login_code field (with a "type the code" fallback
                      inside the picker). Resolves to ``schoolCode`` =
                      login_code, which the login POST below uses unchanged. */}
                  <div className={s.loginField}>
                    <label htmlFor="splash-school-code">
                      {t('login.schoolCode.label')}
                    </label>
                    <SchoolSearchPicker
                      loginCode={schoolCode}
                      onChange={setSchoolCode}
                      inputId="splash-school-code"
                      inputRef={firstFieldRef}
                      formActive={mode === 'login'}
                      testIdPrefix="splash-school"
                    />
                  </div>
                  <div className={s.loginField}>
                    <label htmlFor="splash-student-id">
                      {t('login.studentId.label')}
                    </label>
                    <input
                      id="splash-student-id"
                      type="text"
                      value={studentId}
                      onChange={(e) => setStudentId(e.target.value)}
                      placeholder={t('login.studentId.placeholder')}
                      autoComplete="username"
                      tabIndex={mode === 'login' ? 0 : -1}
                      data-testid="splash-student-id"
                    />
                  </div>
                  <div className={s.loginField}>
                    <label htmlFor="splash-password">
                      {t('login.password.label')}
                    </label>
                    <input
                      id="splash-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t('login.password.placeholder')}
                      autoComplete="current-password"
                      tabIndex={mode === 'login' ? 0 : -1}
                      data-testid="splash-password"
                    />
                  </div>
                </>
              ) : loginMode === 'bootstrap' ? (
                <>
                  {/* Issue #967 legacy bootstrap path — class_code +
                      student_id, no password. Only succeeds while the
                      student has no password yet; the backend then sets
                      ``password_setup_required`` and the handler routes
                      to the set-password screen. */}
                  <div className={`${s.loginField} ${s.loginFieldCode}`}>
                    <label htmlFor="splash-class-code">
                      {t('login.classCode.label')}
                    </label>
                    <input
                      id="splash-class-code"
                      ref={firstFieldRef}
                      type="text"
                      value={classCode}
                      onChange={(e) => setClassCode(e.target.value)}
                      placeholder={t('login.classCode.placeholder')}
                      autoComplete="off"
                      autoCapitalize="characters"
                      tabIndex={mode === 'login' ? 0 : -1}
                      data-testid="splash-class-code"
                    />
                  </div>
                  <div className={s.loginField}>
                    <label htmlFor="splash-student-id-bootstrap">
                      {t('login.studentId.label')}
                    </label>
                    <input
                      id="splash-student-id-bootstrap"
                      type="text"
                      value={studentId}
                      onChange={(e) => setStudentId(e.target.value)}
                      placeholder={t('login.studentId.placeholder')}
                      autoComplete="username"
                      tabIndex={mode === 'login' ? 0 : -1}
                      data-testid="splash-student-id-bootstrap"
                    />
                  </div>
                </>
              ) : forgotSubmitted ? (
                /* ADR 0051 Phase 4 — generic confirmation, identical whether
                   or not the id resolved (no enumeration oracle). */
                <p
                  className={s.loginSubtitle}
                  role="status"
                  data-testid="splash-forgot-confirm"
                >
                  {t('forgot.confirm')}
                </p>
              ) : (
                <>
                  {/* ADR 0051 Phase 4 — forgot-password request: school + id,
                      no password. Posts to the enumeration-safe always-200
                      ``/auth/student-forgot-password``; an admin sees the
                      pending request in their dashboard and resets. */}
                  <div className={s.loginField}>
                    <label htmlFor="splash-forgot-school-code">
                      {t('login.schoolCode.label')}
                    </label>
                    <SchoolSearchPicker
                      loginCode={schoolCode}
                      onChange={setSchoolCode}
                      inputId="splash-forgot-school-code"
                      inputRef={firstFieldRef}
                      formActive={mode === 'login'}
                      testIdPrefix="splash-forgot-school"
                    />
                  </div>
                  <div className={s.loginField}>
                    <label htmlFor="splash-forgot-student-id">
                      {t('login.studentId.label')}
                    </label>
                    <input
                      id="splash-forgot-student-id"
                      type="text"
                      value={studentId}
                      onChange={(e) => setStudentId(e.target.value)}
                      placeholder={t('login.studentId.placeholder')}
                      autoComplete="username"
                      tabIndex={mode === 'login' ? 0 : -1}
                      data-testid="splash-forgot-student-id"
                    />
                  </div>
                </>
              )}
              {loginError && (
                <p className={s.loginError} role="alert">
                  {loginError}
                </p>
              )}
              {/* Submit — hidden once the forgot request is confirmed (only
                  the "back to login" link remains below). */}
              {!(loginMode === 'forgot' && forgotSubmitted) && (
                <button
                  type="submit"
                  className={`${s.btn} ${s.btnPrimary} ${s.loginSubmit}`}
                  data-testid="splash-login-submit"
                  disabled={
                    loginSubmitting || !isLoginComplete || mode !== 'login'
                  }
                  tabIndex={mode === 'login' ? 0 : -1}
                >
                  {loginSubmitting
                    ? t('login.loading')
                    : loginMode === 'forgot'
                      ? `${t('forgot.button')} →`
                      : `${t('login.button')} →`}
                </button>
              )}
              {loginMode === 'forgot' ? (
                /* ADR 0051 Phase 4 — return to the login form. */
                <button
                  type="button"
                  className={s.loginToggle}
                  data-testid="splash-forgot-back"
                  onClick={handleExitForgot}
                  tabIndex={mode === 'login' ? 0 : -1}
                >
                  {t('forgot.backToLogin')}
                </button>
              ) : (
                <>
                  {/* Issue #967 — toggle between the password path and the
                      legacy first-login bootstrap. */}
                  <button
                    type="button"
                    className={s.loginToggle}
                    data-testid="splash-login-toggle"
                    onClick={handleToggleLoginMode}
                    tabIndex={mode === 'login' ? 0 : -1}
                  >
                    {loginMode === 'password'
                      ? t('login.toggle.toBootstrap')
                      : t('login.toggle.toPassword')}
                  </button>
                  {/* ADR 0051 Phase 4 — forgot-password entry (password path
                      only; the bootstrap path is already passwordless). */}
                  {loginMode === 'password' && (
                    <button
                      type="button"
                      className={s.loginToggle}
                      data-testid="splash-forgot-link"
                      onClick={handleEnterForgot}
                      tabIndex={mode === 'login' ? 0 : -1}
                    >
                      {t('forgot.link')}
                    </button>
                  )}
                </>
              )}
            </form>
          </div>
        )}
      </div>

      {/* Admin-signin overlay — split-screen that fades / slides in
          when the admin pill is clicked. Always rendered (so the
          slide animation has a starting frame) but masked from a11y
          + pointer events when the splash is in CTA / login mode. */}
      <div
        className={s.adminOverlay}
        aria-hidden={mode !== 'admin-signin'}
      >
        <aside className={s.adminLeft}>
          <span className={s.adminEyebrow}>
            {t('auth.left.eyebrow.admin')}
          </span>
          <h1 className={s.adminHeadline}>
            {t('auth.left.headline.admin.signin')}
          </h1>
          <p className={s.adminSubhead}>
            {t('auth.left.subhead.admin')}
          </p>
        </aside>
        <main className={s.adminRight}>
          {/* routing="virtual" keeps the Clerk widget contained — no
              URL changes during sign-in; afterSignInUrl handles the
              post-success redirect to /admin. The widget only mounts
              once admin-signin is active to avoid Clerk firing
              telemetry / network calls on every splash visit. */}
          {mode === 'admin-signin' && (
            <SignIn
              routing="virtual"
              signUpUrl="/admin/sign-up"
              afterSignInUrl="/admin"
              afterSignUpUrl="/admin"
            />
          )}
        </main>
      </div>

      {/* Scroll-down affordance — only shown in CTA mode. The marketing
          extension below the fold needs an obvious "there's more" cue;
          without it visitors miss the marketing content entirely.
          Hidden in login mode because the form is already pulling
          focus and a second downward affordance would be noisy. */}
      {mode === 'cta' && (
        <div className={s.scrollHint} aria-hidden="true">
          {t('splash.scrollHint')}
        </div>
      )}
    </div>
  )
}
